/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useTelephonyServersList.tsx
 🗓️ Created: 2025-12-04
 🧩 Description:
    Hook for managing Telephony Servers list for Network Configuration
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { Server } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useAuthStore } from '../../../store/auth';
import { fetchTelephonyServers } from '../../telephonyServers/api';
import type { TelephonyServerWithNetwork } from '../types';

export function useTelephonyServersList() {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug ?? '';
  const userId = user?.id;

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('networkSettings.columns.serverName'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: TelephonyServerWithNetwork) => (
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-primary" />
            <span className="font-semibold text-foreground">{item.name}</span>
          </div>
        ),
      },
      {
        key: 'host',
        label: t('networkSettings.columns.host'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: TelephonyServerWithNetwork) => (
          <span className="font-mono text-sm text-foreground">{item.host}</span>
        ),
      },
      {
        key: 'hostname',
        label: t('networkSettings.columns.hostname'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: TelephonyServerWithNetwork) => (
          <span className="font-mono text-sm text-muted-foreground">{item.hostname || '-'}</span>
        ),
      },
      {
        key: 'ipAddress',
        label: t('networkSettings.columns.ipAddress'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: TelephonyServerWithNetwork) => (
          <span className="font-mono text-sm text-muted-foreground">{item.ipAddress || '-'}</span>
        ),
      },
      {
        key: 'gateway',
        label: t('networkSettings.columns.gateway'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: TelephonyServerWithNetwork) => (
          <span className="font-mono text-sm text-muted-foreground">{item.gateway || '-'}</span>
        ),
      },
      {
        key: 'primaryDns',
        label: t('networkSettings.columns.dns'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: TelephonyServerWithNetwork) => (
          <span className="font-mono text-sm text-muted-foreground">{item.primaryDns || '-'}</span>
        ),
      },
      {
        key: 'status',
        label: t('networkSettings.columns.status'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: TelephonyServerWithNetwork) => {
          const statusClasses = {
            online: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
            offline: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
            error: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
            unknown: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
          };
          
          // In network module, only show server status (SSH/network connectivity)
          // Don't show FreeSWITCH status here
          const statusValue = item.status || 'unknown';
          const status = statusValue.toLowerCase();
          const className = statusClasses[status as keyof typeof statusClasses] || statusClasses.unknown;

          return (
            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${className}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          );
        },
      },
      {
        key: 'actions',
        label: t('common.actions'),
        sortable: false,
        visible: true,
      },
    ],
    [t]
  );

  const fetchData = useMemo(
    () => async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      const result = await fetchTelephonyServers(params, tenantSlug, userId);
      
      // Map servers to include network info
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const serversWithNetwork: TelephonyServerWithNetwork[] = (result.data || []).map((server: any) => ({
        ...server,
        networkConfigured: false, // TODO: Check if server has network config
      }));

      return {
        data: serversWithNetwork,
        pagination: result.pagination,
      };
    },
    [tenantSlug, userId]
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'networkSettings',
  });

  const servers = useMemo(() => data as TelephonyServerWithNetwork[], [data]);

  return {
    servers,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  };
}

